import { useEffect, useState } from "react";
import { apiFetch } from "@shared/api-client";

/**
 * Public dry-hire catalogue (#136): GET /api/inquiry/equipment, tenant
 * resolved server-side (same contract as /api/inquiry/profile). The
 * catalogue is small, so it's fetched once and filtered client-side —
 * the server's ?q= search exists but is not used by the form.
 */
export interface CatalogueItem {
	id: string;
	name: string;
	/** Per-day hire rate in dollars. */
	hireRatePerDay: number;
	/** Product image URL; null when the Company has none uploaded. */
	imageUrl: string | null;
}

function parseCatalogueItem(value: unknown): CatalogueItem | null {
	if (typeof value !== "object" || value === null) return null;
	const record = value as Record<string, unknown>;
	if (typeof record.id !== "string" || typeof record.name !== "string") {
		return null;
	}
	const rate =
		typeof record.hire_rate_per_day === "string"
			? Number(record.hire_rate_per_day)
			: NaN;
	return {
		id: record.id,
		name: record.name,
		hireRatePerDay: Number.isFinite(rate) ? rate : 0,
		imageUrl:
			typeof record.image_url === "string" && record.image_url !== ""
				? record.image_url
				: null,
	};
}

export async function fetchEquipmentCatalogue(
	signal?: AbortSignal,
): Promise<CatalogueItem[]> {
	const res = await apiFetch<{ items?: unknown }>("/api/inquiry/equipment", {
		signal,
	});
	if (!Array.isArray(res.items)) return [];
	return res.items
		.map(parseCatalogueItem)
		.filter((item): item is CatalogueItem => item !== null);
}

export type EquipmentCatalogueState =
	| { status: "loading" }
	| { status: "error"; retry: () => void }
	| { status: "success"; items: CatalogueItem[] };

export function useEquipmentCatalogue(): EquipmentCatalogueState {
	const [attempt, setAttempt] = useState(0);
	const [state, setState] = useState<EquipmentCatalogueState>({
		status: "loading",
	});

	useEffect(() => {
		const controller = new AbortController();
		let active = true;
		const retry = (): void => setAttempt((value) => value + 1);

		async function load(): Promise<void> {
			try {
				const items = await fetchEquipmentCatalogue(controller.signal);
				if (active) setState({ status: "success", items });
			} catch {
				if (!controller.signal.aborted && active) {
					setState({ status: "error", retry });
				}
			}
		}

		setState({ status: "loading" });
		void load();

		return () => {
			active = false;
			controller.abort();
		};
	}, [attempt]);

	return state;
}
